/**
 * Prismatic Spark — shatter N random gears into rainbow shards (N scales
 * with enhancement level, see mutations/levels.ts), biased toward "support"
 * gears whose removal strands other gears. Then run the standard
 * floating-drop pass so dependents come crashing down.
 */

import type { PRNG } from '../../rng.js';
import type { BoardState, HexCoord, MutationCard } from '../../types.js';
import { removeBubbles } from '../../types.js';
import { findSupportGears, findFloatingClusters } from '../../grid/cluster.js';
import { MutationType } from '../registry.js';
import { enhancementMagnitude } from '../levels.js';

/** Mk. I shatter count. */
export const PRISMATIC_SPARK_BASE_COUNT = enhancementMagnitude(MutationType.RainbowRow, 1);

/**
 * Deterministic result of computing Prismatic Spark targets — used by both
 * the board-apply path and the renderer VFX path.
 */
export interface PrismaticSparkTargets {
  /** The gears chosen to shatter. */
  readonly chosen: HexCoord[];
  /** Bubbles that become floating after chosen gears are removed (will drop). */
  readonly floating: HexCoord[];
}

/**
 * Pick the gears to shatter. Weighted toward support gears; if fewer than
 * `count` supports exist, fall back to all colored (non-obstacle) coords.
 */
export function pickPrismaticSparkTargets(
  board: BoardState,
  rng: PRNG,
  count: number = PRISMATIC_SPARK_BASE_COUNT,
): PrismaticSparkTargets {
  const supports = findSupportGears(board);

  const allColored: HexCoord[] = [];
  for (const [, bubble] of board.bubbles) {
    if (!bubble.isObstacle) allColored.push(bubble.coord);
  }

  const pickFrom = supports.length >= count ? supports : allColored;
  if (pickFrom.length === 0) {
    return { chosen: [], floating: [] };
  }

  const n = Math.min(count, pickFrom.length);
  const pool = [...pickFrom];
  const chosen: HexCoord[] = [];
  for (let i = 0; i < n; i++) {
    const idx = rng.nextInt(0, pool.length - 1);
    chosen.push(pool[idx]!);
    pool.splice(idx, 1);
  }

  const afterShatter = removeBubbles(board, chosen);
  const floating = findFloatingClusters(afterShatter);

  return { chosen, floating };
}

export function applyRainbowRow(
  board: BoardState,
  card: MutationCard,
  rng: PRNG,
): BoardState {
  const count = enhancementMagnitude(MutationType.RainbowRow, card.level);
  const { chosen, floating } = pickPrismaticSparkTargets(board, rng, count);
  if (chosen.length === 0) return board;

  const afterShatter = removeBubbles(board, chosen);
  if (floating.length === 0) return afterShatter;
  return removeBubbles(afterShatter, floating);
}
